'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { cn } from '@/core/utils/cn';
import { useLocalization } from '@/core/hooks/useLocalization';
import {
  ChevronLeft, Star, Users, BedDouble, Maximize, Sparkles,
  Check, Shield, ChevronRight,
} from 'lucide-react';
import { Select } from '@/shared/ui';
import { DateRangePicker } from '@/shared/forms';
import { useHotelRoomTypes, usePublicHotels } from '../hooks/useHotels';
import type { HotelRoomTypeData } from '../types/hotels.types';
import { createQueryString, findHotelBySlug, withQuery } from '../hotel-routing';
import { computeGST } from '@/core/constants/taxes';
import { useTranslation } from '@/core/i18n';

/* ─── Props ──────────────────────────────────────────────────────────── */

interface RoomDetailProps {
  hotelSlug: string;
  roomSlug: string;
}

/* ─── Star Rating ────────────────────────────────────────────────────── */

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          className={cn(
            i < Math.round(rating)
              ? 'fill-hms-gold text-hms-gold'
              : 'fill-transparent text-hms-textDim/40',
          )}
        />
      ))}
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────── */

export function RoomDetail({ hotelSlug, roomSlug }: RoomDetailProps) {
  const { t } = useTranslation();
  const { formatMoney } = useLocalization();
  const searchParams = useSearchParams();
  const [activePhoto, setActivePhoto] = useState(0);

  /* ── Booking form state (init from URL params) ── */
  const [checkIn, setCheckIn] = useState(() => searchParams.get('checkIn') || '');
  const [checkOut, setCheckOut] = useState(() => searchParams.get('checkOut') || '');
  const [guests, setGuests] = useState(() => searchParams.get('guests') || '2');

  const { data: roomTypesData } = useHotelRoomTypes();
  const { data: allHotels } = usePublicHotels();
  const room = useMemo(
    () => {
      const hotelRoomTypes = ((roomTypesData as unknown as Record<string, unknown>)?.data ?? []) as HotelRoomTypeData[];
      return hotelRoomTypes.find((r) => r.slug === roomSlug) ?? null;
    },
    [roomSlug, roomTypesData],
  );
  const hotel = useMemo(
    () => findHotelBySlug(hotelSlug, allHotels ?? []),
    [hotelSlug, allHotels],
  );
  const hotelName = hotel?.name
    ?? hotelSlug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const resultsQuery = useMemo(() => createQueryString({
    q: searchParams.get('q') || '',
    checkIn,
    checkOut,
    guests,
  }), [searchParams, checkIn, checkOut, guests]);
  const hotelHref = useMemo(
    () => withQuery(`/hotels/${hotelSlug}`, resultsQuery),
    [hotelSlug, resultsQuery],
  );

  const hasDates = !!(checkIn && checkOut);

  const nights = useMemo(() => {
    if (!hasDates) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.ceil(diff / 86400000));
  }, [checkIn, checkOut, hasDates]);

  const bookUrl = useMemo(() => withQuery(
    `/hotels/${hotelSlug}/book`,
    createQueryString({
      q: searchParams.get('q') || '',
      checkIn,
      checkOut,
      guests,
      room: roomSlug,
    }),
  ), [searchParams, hotelSlug, roomSlug, checkIn, checkOut, guests]);

  const [todayStr, setTodayStr] = useState('');
  // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only initialization from browser Date API
  useEffect(() => { setTodayStr(new Date().toISOString().split('T')[0]); }, []);

  const discount = room ? Math.round(((room.originalPrice - room.pricePerNight) / room.originalPrice) * 100) : 0;
  const subtotal = room ? room.pricePerNight * (nights > 0 ? nights : 1) : 0;
  const gstCalc = computeGST(subtotal, 'room', room ? room.pricePerNight : 0);
  const taxes = gstCalc.total;
  const total = subtotal + taxes;

  const handlePhotoNav = useCallback((dir: 1 | -1) => {
    if (!room) return;
    const len = room.gallery.length;
    setActivePhoto((p) => (p + dir + len) % len);
  }, [room]);

  useEffect(() => {
    if (!checkIn || !checkOut) return;
    if (new Date(checkOut).getTime() <= new Date(checkIn).getTime()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync derived validation: clear invalid checkout when checkin changes
      setCheckOut('');
    }
  }, [checkIn, checkOut]);

  /* ── Not found ── */
  if (!room) {
    return (
      <div id="hotels-room-detail-not-found" className="min-h-[calc(100vh-4rem)] bg-hms-bg flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          {/* Decorative icon */}
          <div className="relative mx-auto w-24 h-24 mb-8">
            <div className="absolute inset-0 rounded-2xl bg-hms-surface border border-hms-border rotate-6" />
            <div className="absolute inset-0 rounded-2xl bg-hms-card border border-hms-border -rotate-3 flex items-center justify-center">
              <BedDouble className="w-10 h-10 text-hms-textDim" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-hms-text mb-3 tracking-tight">{t('roomDetail.notFoundTitle')}</h1>
          <p className="text-sm text-hms-textSub leading-relaxed mb-2">
            {t('roomDetail.notFoundDescription')}
          </p>
          <p className="text-xs text-hms-textDim mb-8">
            {t('roomDetail.tryBrowsing', { hotelName })}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              id="hotels-room-detail-back-fallback"
              href={hotelHref}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-hms-gold text-black text-sm font-bold hover:brightness-110 transition-all"
            >
              <ChevronLeft size={16} />
              {t('roomDetail.viewRoomsAt', { hotelName })}
            </Link>
            <Link
              id="hotels-room-detail-back-hotels"
              href="/hotels"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl border border-hms-border text-sm font-medium text-hms-textSub hover:border-hms-borderLt hover:text-hms-text transition-colors"
            >
              {t('roomDetail.browseAllHotels')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const allPhotos = [room.photo, ...room.gallery];

  return (
    <div id="hotels-room-detail-page" className="min-h-screen bg-hms-bg">
      {/* ── Top bar ── */}
      <div id="hotels-room-detail-topbar" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4 flex items-center justify-between">
        <Link
          id="hotels-room-detail-back"
          href={hotelHref}
          className="inline-flex items-center gap-1.5 text-sm text-hms-textSub hover:text-hms-gold transition-colors"
        >
          <ChevronLeft size={16} />
          {t('roomDetail.backTo', { hotelName })}
        </Link>
        <span className="text-[10px] font-bold uppercase tracking-widest text-hms-textDim">{hotelName}</span>
      </div>

      {/* ── Photo Gallery ── */}
      <div id="hotels-room-detail-gallery" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 overflow-hidden">
          {/* Main photo */}
          <div className="relative md:col-span-2 h-64 md:h-[380px] min-h-0 overflow-hidden rounded-xl">
            <img
              id="hotels-room-detail-main-photo"
              src={allPhotos[activePhoto]}
              alt={`${room.name} - Photo ${activePhoto + 1}`}
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
            />
            {discount > 0 && (
              <div className="absolute top-3 left-3 bg-hms-red text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
                {discount}{t('roomDetail.percentOff')}
              </div>
            )}
            {/* Nav arrows */}
            <button
              id="hotels-room-detail-photo-prev"
              type="button"
              onClick={() => handlePhotoNav(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-hms-bg/70 backdrop-blur-sm border border-hms-border flex items-center justify-center hover:bg-hms-bg transition-colors"
            >
              <ChevronLeft size={16} className="text-hms-text" />
            </button>
            <button
              id="hotels-room-detail-photo-next"
              type="button"
              onClick={() => handlePhotoNav(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-hms-bg/70 backdrop-blur-sm border border-hms-border flex items-center justify-center hover:bg-hms-bg transition-colors"
            >
              <ChevronRight size={16} className="text-hms-text" />
            </button>
            {/* Photo counter */}
            <div className="absolute bottom-3 right-3 bg-hms-bg/70 backdrop-blur-sm border border-hms-border rounded-lg px-3 py-1.5 text-[10px] font-semibold text-hms-text">
              {activePhoto + 1} / {allPhotos.length}
            </div>
          </div>

          {/* Thumbnail grid */}
          <div className="hidden md:grid md:col-span-2 grid-cols-2 gap-2 min-h-0">
            {room.gallery.map((src, i) => (
              <button
                key={i}
                id={`hotels-room-detail-thumb-${i}`}
                type="button"
                onClick={() => setActivePhoto(i + 1)}
                className={cn(
                  'relative h-[186px] min-h-0 overflow-hidden rounded-xl transition-all',
                  activePhoto === i + 1 ? 'ring-2 ring-hms-gold' : 'opacity-80 hover:opacity-100',
                )}
              >
                <Image src={src} alt={`${room.name} - View ${i + 2}`} fill sizes="(max-width: 768px) 50vw, 25vw" className="object-cover" unoptimized />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div id="hotels-room-detail-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* ── Left column ── */}
          <div id="hotels-room-detail-info" className="flex-1 min-w-0 space-y-8">
            {/* Room heading */}
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-widest text-hms-gold bg-hms-gold/10 px-2.5 py-1 rounded-full">
                  {room.filterGroup}
                </span>
                {room.available <= 3 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-hms-red bg-hms-red/10 px-2.5 py-1 rounded-full">
                    {t('roomDetail.onlyNLeft', { n: String(room.available), s: '' })}
                  </span>
                )}
              </div>
              <h1 id="hotels-room-detail-name" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-hms-text tracking-tight">
                {room.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3">
                <div className="flex items-center gap-2">
                  <StarRating rating={room.rating} />
                  <span className="text-sm font-semibold text-hms-gold">{room.rating}</span>
                </div>
                <span className="text-sm text-hms-textDim">{room.reviewCount} {t('roomDetail.reviews')}</span>
              </div>
            </div>

            <hr className="border-hms-border" />

            {/* Room specs */}
            <div id="hotels-room-detail-specs" className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              {[
                { icon: Maximize, label: t('roomDetail.roomSize'), value: room.size },
                { icon: BedDouble, label: t('roomDetail.bedType'), value: room.bedType },
                { icon: Users, label: t('roomDetail.maxGuests'), value: t('roomDetail.upToGuests', { count: String(room.maxGuests) }) },
                { icon: Star, label: t('roomDetail.rating'), value: t('roomDetail.ratingOutOf', { rating: String(room.rating) }) },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex flex-col items-center gap-2 rounded-xl border border-hms-border bg-hms-card px-3 py-4 text-center">
                  <Icon size={20} className="text-hms-gold" />
                  <span className="text-[10px] font-semibold text-hms-textDim uppercase tracking-wider">{label}</span>
                  <span className="text-xs font-bold text-hms-text">{value}</span>
                </div>
              ))}
            </div>

            <hr className="border-hms-border" />

            {/* Description */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-hms-text">{t('roomDetail.aboutThisRoom')}</h2>
              <p id="hotels-room-detail-description" className="text-sm leading-relaxed text-hms-textSub">
                {room.fullDescription}
              </p>
            </div>

            <hr className="border-hms-border" />

            {/* Amenities */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-hms-text">{t('roomDetail.roomAmenities')}</h2>
              <div id="hotels-room-detail-amenities" className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {room.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2.5 rounded-lg border border-hms-border bg-hms-card px-3.5 py-3">
                    <Check size={14} className="text-hms-gold shrink-0" />
                    <span className="text-xs font-medium text-hms-textSub">{a}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            {room.highlights.length > 0 && (
              <>
                <hr className="border-hms-border" />
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-hms-text">{t('roomDetail.highlights')}</h2>
                  <div id="hotels-room-detail-highlights" className="grid grid-cols-2 gap-2.5">
                    {room.highlights.map((h) => (
                      <div key={h} className="flex items-center gap-2.5 rounded-lg border border-hms-teal/20 bg-hms-teal/[0.04] px-3.5 py-3">
                        <Sparkles size={14} className="text-hms-teal shrink-0" />
                        <span className="text-xs font-medium text-hms-teal">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <hr className="border-hms-border" />

            {/* Room Policies */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-hms-text">{t('roomDetail.roomPolicies')}</h2>
              <div id="hotels-room-detail-policies" className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-3">
                {room.policies.map((policy) => (
                  <div key={policy} className="flex items-start gap-2.5">
                    <Shield size={14} className="text-hms-gold shrink-0 mt-0.5" />
                    <span className="text-xs text-hms-textSub leading-relaxed">{policy}</span>
                  </div>
                ))}
              </div>
            </div>

            <hr className="border-hms-border" />

            {/* Reviews */}
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-hms-text">{t('roomDetail.guestReviews')}</h2>
                <div className="flex items-center gap-2">
                  <span className="flex items-center justify-center h-8 w-8 rounded-lg bg-hms-gold text-black text-sm font-bold">
                    {room.rating}
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-hms-text leading-none">
                      {room.rating >= 4.8 ? t('roomDetail.exceptional') : room.rating >= 4.5 ? t('roomDetail.excellent') : t('roomDetail.veryGood')}
                    </p>
                    <p className="text-[10px] text-hms-textDim mt-0.5">{room.reviewCount} {t('roomDetail.reviews')}</p>
                  </div>
                </div>
              </div>
              <div id="hotels-room-detail-reviews" className="space-y-4">
                {room.reviews.map((review) => (
                  <div key={review.name} className="rounded-xl border border-hms-border bg-hms-card p-5 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-hms-gold/20 to-hms-gold/5 border border-hms-gold/20">
                        <span className="text-xs font-bold text-hms-gold">{review.initials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-hms-text truncate">{review.name}</p>
                        <p className="text-[11px] text-hms-textDim">{review.date}</p>
                      </div>
                      <StarRating rating={review.rating} size={12} />
                    </div>
                    <p className="text-sm leading-relaxed text-hms-textSub">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right column — Booking card ── */}
          <div id="hotels-room-detail-sidebar" className="w-full lg:w-[380px] shrink-0">
            <div className="sticky top-24 rounded-2xl border border-hms-border bg-hms-card p-6 space-y-5 shadow-xl shadow-black/20">
              {/* Price */}
              <div className="flex items-baseline gap-2.5 flex-wrap">
                <span className="text-2xl font-bold text-hms-gold">{formatMoney(room.pricePerNight)}</span>
                <span className="text-sm text-hms-textDim line-through">{formatMoney(room.originalPrice)}</span>
                <span className="text-sm text-hms-text">{t('roomDetail.perNight')}</span>
              </div>

              <hr className="border-hms-border/50" />

              {/* Date & guest inputs */}
              <div className="space-y-3">
                <DateRangePicker
                  id="hotels-room-detail-dates"
                  label={t('common.checkInCheckOut')}
                  startDate={checkIn}
                  endDate={checkOut}
                  onChange={(start, end) => { setCheckIn(start); setCheckOut(end); }}
                  minDate={todayStr}
                />
                <Select
                  id="hotels-room-detail-guests"
                  label={t('roomDetail.guests')}
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  options={Array.from({ length: room.maxGuests }, (_, i) => i + 1).map((n) => ({ label: n === 1 ? t('roomDetail.oneGuest') : t('roomDetail.nGuests', { n: String(n) }), value: String(n) }))}
                />
              </div>

              {/* Stay summary */}
              {nights > 0 ? (
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-hms-textSub">{formatMoney(room.pricePerNight)} &times; {nights} {nights !== 1 ? t('roomDetail.nights') : t('roomDetail.night')}</span>
                    <span className="text-xs font-medium text-hms-text">{formatMoney(room.pricePerNight * nights)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-hms-green">{t('roomDetail.youSave', { discount: String(discount) })}</span>
                      <span className="text-xs font-medium text-hms-green">{formatMoney((room.originalPrice - room.pricePerNight) * nights)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-hms-textSub">{t('roomDetail.taxesAndFees')}</span>
                    <span className="text-xs font-medium text-hms-text">{formatMoney(taxes)}</span>
                  </div>
                  <hr className="border-hms-border/50" />
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-hms-text">{t('roomDetail.total')}</span>
                    <span className="text-base font-bold text-hms-gold">{formatMoney(total)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-hms-textDim text-center py-2">{t('roomDetail.selectDatesPriceBreakdown')}</p>
              )}

              {/* Room info */}
              <div className="rounded-lg bg-hms-surface p-4 border border-hms-border/50 space-y-2">
                <p className="text-xs font-bold text-hms-gold">{room.name}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-hms-textSub">
                  <span className="inline-flex items-center gap-1"><Maximize size={11} /> {room.size}</span>
                  <span className="inline-flex items-center gap-1"><BedDouble size={11} /> {room.bedType}</span>
                  <span className="inline-flex items-center gap-1"><Users size={11} /> {t('roomDetail.upToGuests', { count: String(room.maxGuests) })}</span>
                </div>
              </div>

              {/* Availability */}
              <div className="flex items-center justify-between text-xs">
                <span className="text-hms-textSub">{t('roomDetail.availability')}</span>
                <span className={cn('font-semibold', room.available <= 3 ? 'text-hms-red' : 'text-hms-green')}>
                  {room.available} {room.available !== 1 ? t('roomDetail.rooms') : t('roomDetail.room')} {t('roomDetail.roomsLeft')}
                </span>
              </div>

              <hr className="border-hms-border/50" />

              {/* CTA */}
              {hasDates && nights > 0 ? (
                <Link
                  id="hotels-room-detail-reserve"
                  href={bookUrl}
                  className="flex items-center justify-center w-full h-12 rounded-xl bg-hms-gold text-black text-sm font-bold tracking-wide hover:brightness-110 active:brightness-95 transition-all cursor-pointer"
                >
                  {t('roomDetail.reserveNow')}
                </Link>
              ) : (
                <div
                  id="hotels-room-detail-reserve"
                  className="flex items-center justify-center w-full h-12 rounded-xl bg-hms-gold/20 border border-hms-gold/30 text-hms-textDim text-sm font-bold tracking-wide cursor-not-allowed"
                >
                  {t('roomDetail.selectDatesToBook')}
                </div>
              )}

              <p className="text-center text-[11px] text-hms-textDim">
                {t('roomDetail.noChargeYet')}
              </p>

              {/* Back to all rooms */}
              <Link
                id="hotels-room-detail-all-rooms"
                href={hotelHref}
                className="flex items-center justify-center w-full h-10 rounded-xl border border-hms-border text-xs font-medium text-hms-textSub hover:border-hms-borderLt hover:text-hms-text transition-colors"
              >
                {t('roomDetail.viewAllRoomTypes')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
